import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import './shared/styles/flow.css';
import Layout from './shared/components/Layout';
import { BookingsProvider } from './shared/booking/BookingsContext';
import Home from './modules/home/Home';
import MyTrips from './modules/account/MyTrips';
import FlightRoutes from './modules/flights/FlightRoutes';
import HotelRoutes from './modules/hotels/HotelRoutes';
import PackageRoutes from './modules/packages/PackageRoutes';
import SightseeingRoutes from './modules/sightseeing/SightseeingRoutes';
import TransferRoutes from './modules/transfers/TransferRoutes';
import InsuranceRoutes from './modules/insurance/InsuranceRoutes';
import AccountPage from './modules/account/AccountPage';
import AdminUsersPage from './modules/account/AdminUsersPage';
import AdminPackagesPage from './modules/admin/AdminPackagesPage';
import PackageFormPage from './modules/admin/PackageFormPage';
import AdminEnquiriesPage from './modules/admin/AdminEnquiriesPage';
import AdminPromoCodesPage from './modules/admin/AdminPromoCodesPage';
import PromoCodeFormPage from './modules/admin/PromoCodeFormPage';
import AdminFeesPage from './modules/admin/AdminFeesPage';
import { PackagesProvider } from './modules/packages/PackagesContext';
import { AuthProvider } from './auth/AuthContext';
import { useCurrencySync } from './shared/currency/CurrencySelect';
import AdminCurrenciesPage from './modules/admin/AdminCurrenciesPage';
import AdminSupplierSettingsPage from './modules/admin/AdminSupplierSettingsPage';
import AdminBookingsPage from './modules/admin/AdminBookingsPage';
import { AdminHome } from './modules/admin/AdminShell';
import AdminAmendmentsPage from './modules/admin/AdminAmendmentsPage';
import AdminReportsPage from './modules/admin/AdminReportsPage';
import DocumentPage from './shared/documents/DocumentPage';
import BusRoutes from './modules/bus/BusRoutes';
import { BookingTerms, CancellationPolicy, PrivacyPolicy } from './shared/legal/LegalPages';

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname, search]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <AppTree />
    </AuthProvider>
  );
}

// Re-renders the whole page tree when the display currency changes, so every price follows it.
function AppTree() {
  useCurrencySync();
  return (
    <BookingsProvider>
      <PackagesProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* E-tickets and vouchers print as standalone documents, without the site header and footer. */}
            <Route path="documents/:bookingId" element={<DocumentPage />} />
            <Route element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="flights/*" element={<FlightRoutes />} />
              <Route path="hotels/*" element={<HotelRoutes />} />
              <Route path="packages/*" element={<PackageRoutes />} />
              <Route path="sightseeing/*" element={<SightseeingRoutes />} />
              <Route path="transfers/*" element={<TransferRoutes />} />
              <Route path="insurance/*" element={<InsuranceRoutes />} />
              <Route path="bus/*" element={<BusRoutes />} />
              <Route path="my-trips" element={<MyTrips />} />
              <Route path="terms" element={<BookingTerms />} />
              <Route path="cancellation-policy" element={<CancellationPolicy />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="account/:mode" element={<AccountPage />} />
              <Route path="admin" element={<AdminHome />} />
              <Route path="admin/bookings" element={<AdminBookingsPage />} />
              <Route path="admin/amendments" element={<AdminAmendmentsPage />} />
              <Route path="admin/reports" element={<AdminReportsPage />} />
              <Route path="admin/users" element={<AdminUsersPage />} />
              <Route path="admin/packages" element={<AdminPackagesPage />} />
              <Route path="admin/packages/new" element={<PackageFormPage />} />
              <Route path="admin/packages/:packageId/edit" element={<PackageFormPage />} />
              <Route path="admin/enquiries" element={<AdminEnquiriesPage />} />
              <Route path="admin/promo-codes" element={<AdminPromoCodesPage />} />
              <Route path="admin/promo-codes/new" element={<PromoCodeFormPage />} />
              <Route path="admin/promo-codes/:promoCodeId/edit" element={<PromoCodeFormPage />} />
              <Route path="admin/fees" element={<AdminFeesPage />} />
              <Route path="admin/currencies" element={<AdminCurrenciesPage />} />
              <Route path="admin/supplier-settings" element={<AdminSupplierSettingsPage />} />
              <Route path="*" element={<Home />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </PackagesProvider>
    </BookingsProvider>
  );
}
